/**
 * SettingsEntityFormDialog — shared form dialog wrapper for workspace settings.
 *
 * Provides a consistent Title + Body + Save/Cancel footer layout, replacing
 * ad-hoc inline-flex style blocks across groups and credentials modals.
 */
import { Button } from '@affine/component';
import { Modal } from '@affine/component/ui/modal';
import type { ReactNode } from 'react';

import * as styles from './settings-form.css';

export interface SettingsEntityFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Modal width in px. Default: 480 */
  width?: number;
  /** Form body content */
  children: ReactNode;
  onSave: () => void;
  /** Defaults to onOpenChange(false) */
  onCancel?: () => void;
  saveLabel: ReactNode;
  cancelLabel: ReactNode;
  saveDisabled?: boolean;
  saving?: boolean;
  /** Label shown while saving=true. Falls back to saveLabel. */
  savingLabel?: ReactNode;
  /** Button variant for Save button. Default: 'primary' */
  saveVariant?: 'primary' | 'error';
  contentTestId?: string;
  saveTestId?: string;
}

export function SettingsEntityFormDialog({
  open,
  onOpenChange,
  title,
  width = 480,
  children,
  onSave,
  onCancel,
  saveLabel,
  cancelLabel,
  saveDisabled = false,
  saving = false,
  savingLabel,
  saveVariant = 'primary',
  contentTestId,
  saveTestId,
}: SettingsEntityFormDialogProps) {
  const handleCancel = onCancel ?? (() => onOpenChange(false));

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={title} width={width}>
      <div className={styles.formDialogBody} data-testid={contentTestId}>
        {children}
        <div className={styles.formDialogFooter}>
          <Button onClick={handleCancel}>{cancelLabel}</Button>
          <Button
            data-testid={saveTestId}
            variant={saveVariant}
            disabled={saveDisabled || saving}
            onClick={onSave}
          >
            {saving ? (savingLabel ?? saveLabel) : saveLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
